/**
 * axios GET deduper — coalesces concurrent identical GETs to safe,
 * cacheable endpoints so the frontend never fires the same read
 * request twice in flight.
 *
 * iter284 Phase B — Fixes the "N components fetch /api/settings/public
 * on mount" anti-pattern without refactoring 11 callers. The dedupe is
 * strictly opt-in via an allowlist of endpoints known to be safe
 * (public, no side-effects, cheap on the backend).
 *
 * Also provides a short client-side memory TTL to short-circuit
 * repeated fetches within a page session (default 15s). This lets an
 * app-level Provider prefetch once and any child component that still
 * calls axios.get() will resolve instantly from the cache — no extra
 * network trip.
 *
 * NOTHING sensitive goes through this dedupe. The backend remains the
 * single source of truth for balances, KYC, ledger, secrets, etc.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Endpoints that are safe to dedupe + client-cache briefly. All are
// public JAPAP configuration / rates endpoints. NEVER add wallet, KYC,
// or admin endpoints here.
const DEDUPE_ALLOWLIST = [
  '/api/settings/public',
  '/api/currency/rates',
  '/api/currency/detect',
  '/api/pro/upsell-config',
  '/api/forecast/settings/public',
  '/api/watch-earn/settings/public',
];

// TTL (ms) applied to the client-side memory cache. Kept short so an
// admin toggle in the Dashboard becomes visible within seconds.
const CACHE_TTL_MS = 15_000;

const inflight = new Map();  // key: normalized URL → Promise<AxiosResponse>
const memCache = new Map();  // key: normalized URL → { at, res }

function normalizeUrl(rawUrl) {
  if (!rawUrl) return '';
  // Strip host if the caller passed the full REACT_APP_BACKEND_URL prefix.
  let u = rawUrl;
  if (API && u.startsWith(API)) u = u.slice(API.length);
  // Drop query string for allowlist matching (safe: these endpoints
  // ignore query variance in practice).
  const path = u.split('?')[0];
  return { path, key: u };
}

function isDedupable(config) {
  if (!config) return false;
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') return false;
  const { path } = normalizeUrl(config.url);
  return DEDUPE_ALLOWLIST.includes(path);
}

axios.interceptors.request.use(async (config) => {
  if (!isDedupable(config)) return config;
  const { key } = normalizeUrl(config.url);

  // 1. Fresh cache hit → resolve the request early (return a fake
  //    "cancelled" adapter that yields the cached response).
  const cached = memCache.get(key);
  if (cached && (Date.now() - cached.at) < CACHE_TTL_MS) {
    // eslint-disable-next-line no-param-reassign
    config.adapter = async () => ({
      ...cached.res,
      config,
      request: cached.res.request || null,
      __fromDedupe: 'cache',
    });
    return config;
  }

  // 2. NOTE (QA iter298e): the previous "in-flight coalesce" branch here
  //    dead-locked the FIRST caller. `dedupedGet()` below registers its own
  //    promise in `inflight` synchronously, before this async interceptor
  //    runs, so the interceptor saw the caller's OWN promise and made the
  //    adapter await itself → the request was never dispatched and the
  //    promise never settled (broke /api/settings/public app-wide).
  //    Coalescing is already handled in `dedupedGet()`; keep the interceptor
  //    limited to the fresh-cache short-circuit above.
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const cfg = response?.config;
    if (isDedupable(cfg)) {
      const { key } = normalizeUrl(cfg.url);
      if (!response.__fromDedupe) {
        memCache.set(key, { at: Date.now(), res: response });
      }
    }
    return response;
  },
  (error) => {
    // Never cache errors — let the caller retry.
    const cfg = error?.config;
    if (cfg && isDedupable(cfg)) {
      const { key } = normalizeUrl(cfg.url);
      inflight.delete(key);
    }
    return Promise.reject(error);
  },
);

// Track raw axios GETs to allowlisted endpoints and register the
// promise BEFORE the interceptor rewrites the adapter, so any parallel
// caller lands on `inflight` and shares the promise.
const origGet = axios.get.bind(axios);
axios.get = function dedupedGet(url, config) {
  const { path, key } = normalizeUrl(url);
  if (!DEDUPE_ALLOWLIST.includes(path)) return origGet(url, config);
  // Serve fresh cache immediately without touching the network.
  const cached = memCache.get(key);
  if (cached && (Date.now() - cached.at) < CACHE_TTL_MS) {
    return Promise.resolve({ ...cached.res });
  }
  if (inflight.has(key)) return inflight.get(key);
  const p = origGet(url, config)
    .then((res) => { memCache.set(key, { at: Date.now(), res }); return res; })
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
};

export function bustSettingsCache(path) {
  if (path) {
    memCache.delete(path);
    return;
  }
  memCache.clear();
}
